<script>
import { defineAsyncComponent, ref, computed } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components/widgets/FloatingCallWidget.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

import MobileTabBar from 'dashboard/components-next/navigation/MobileTabBar.vue';
import { useCallsStore } from 'dashboard/stores/calls';

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    FloatingCallWidget,
    MobileTabBar,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId, accountScopedRoute } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const callsStore = useCallsStore();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      accountScopedRoute,
      upgradePageRef,
      windowWidth,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
      mobileActiveTab: null,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
    resolvedMobileActiveTab() {
      if (this.mobileActiveTab) {
        return this.mobileActiveTab;
      }

      if (this.$route.name === 'search') {
        return 'search';
      }

      if (this.$route.name === 'tasks_dashboard_index') {
        return 'tasks';
      }

      if (this.$route.name === 'products_dashboard_index') {
        return 'products';
      }

      const contactRouteNames = [
        'contacts_dashboard_index',
        'contacts_dashboard_segments_index',
        'contacts_dashboard_labels_index',
        'contacts_dashboard_active',
        'contacts_crm_events',
        'contacts_edit',
        'contacts_edit_segment',
        'contacts_edit_label',
      ];
      const conversationRouteNames = [
        'home',
        'inbox_conversation',
        'inbox_dashboard',
        'conversation_through_inbox',
        'label_conversations',
        'conversations_through_label',
        'team_conversations',
        'conversations_through_team',
        'folder_conversations',
        'conversations_through_folders',
        'conversation_mentions',
        'conversation_through_mentions',
        'conversation_unattended',
        'conversation_through_unattended',
        'conversation_participating',
        'conversation_through_participating',
        'inbox_view',
        'inbox_view_conversation',
      ];

      if (contactRouteNames.includes(this.$route.name)) {
        return 'contact';
      }

      return conversationRouteNames.includes(this.$route.name)
        ? 'communications'
        : 'tasks';
    },
    isConversationDetailPage() {
      return [
        'inbox_conversation',
        'conversation_through_inbox',
        'conversations_through_label',
        'conversations_through_team',
        'conversations_through_folders',
        'conversation_through_mentions',
        'conversation_through_unattended',
        'conversation_through_participating',
        'inbox_view_conversation',
      ].includes(this.$route.name);
    },
    showMobileTabBar() {
      return !this.isConversationDetailPage;
    },
    shouldAddMobileTabBarScrollSpace() {
      return this.isSmallScreen && this.showMobileTabBar;
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
    '$route.fullPath'() {
      if (this.isSmallScreen && this.isMobileSidebarOpen) {
        this.closeMobileSidebar();
      }
    },
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;

      if (!this.isMobileSidebarOpen && this.mobileActiveTab === 'navigation') {
        this.mobileActiveTab = null;
      }
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;

      if (this.mobileActiveTab === 'navigation') {
        this.mobileActiveTab = null;
      }
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
    handleMobileTabChange(tab) {
      this.mobileActiveTab = tab;

      if (tab === 'search') {
        this.closeMobileSidebar();
        this.$router.push(this.accountScopedRoute('search'));
        return;
      }

      if (tab === 'communications') {
        this.closeMobileSidebar();
        this.$router.push(this.accountScopedRoute('home'));
        return;
      }

      if (tab === 'contact') {
        this.closeMobileSidebar();
        this.$router.push(
          this.accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          )
        );
        return;
      }

      if (tab === 'tasks') {
        this.closeMobileSidebar();
        this.$router.push(this.accountScopedRoute('tasks_dashboard_index'));
        return;
      }

      if (tab === 'products') {
        this.closeMobileSidebar();
        this.$router.push(this.accountScopedRoute('products_dashboard_index'));
      }
    },
    handleOpenMobileNavigation() {
      this.mobileActiveTab = 'navigation';
      this.toggleMobileSidebar();
    },
  },
};
</script>

<template>
  <div class="flex flex-grow overflow-hidden text-n-slate-12">
    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main
      class="flex flex-1 h-full w-full min-h-0 px-0 overflow-hidden bg-n-background lg:bg-n-surface-1"
    >
      <UpgradePage
        v-show="showUpgradePage"
        ref="upgradePageRef"
        :bypass-upgrade-page="bypassUpgradePage"
      >
        <MobileTabBar
          v-if="showMobileTabBar"
          :active-tab="resolvedMobileActiveTab"
          :on-tab-change="handleMobileTabChange"
          :on-open-navigation="handleOpenMobileNavigation"
        />
      </UpgradePage>
      <template v-if="!showUpgradePage">
        <div
          class="flex h-full w-full min-h-0 min-w-0 flex-1 overflow-hidden"
          :class="{
            'mobile-tab-bar-scroll-area': shouldAddMobileTabBarScrollSpace,
          }"
        >
          <router-view />
        </div>
        <CommandBar />
        <CopilotLauncher />
        <MobileTabBar
          v-if="showMobileTabBar"
          :active-tab="resolvedMobileActiveTab"
          :on-tab-change="handleMobileTabChange"
          :on-open-navigation="handleOpenMobileNavigation"
        />
        <CopilotContainer />
        <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
      </template>
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
  </div>
</template>

<style>
.mobile-tab-bar-scroll-area {
  --mobile-tab-bar-end-space: calc(env(safe-area-inset-bottom, 0px) + 5.75rem);
}

@media (max-width: 767px) {
  .mobile-tab-bar-scroll-area
    :where(
      .overflow-auto,
      .overflow-y-auto,
      .overflow-y-scroll,
      .overflow-scroll
    ):where(.h-full, .flex-1, .flex-grow, .min-h-0, [class*='h-[calc']):where(
      :not(.flex),
      .flex-col
    )::after {
    content: '';
    display: block;
    width: 100%;
    height: var(--mobile-tab-bar-end-space);
    flex: 0 0 var(--mobile-tab-bar-end-space);
  }
}
</style>
